import type { FC } from "react";

import type { TaskLogEntry } from "../../../types";

import { formatRelativeTime } from "../../../utils/time";
import { Badge } from "../../common/Badge";
import { SectionHeader } from "../../common/SectionHeader";

export function getTraceBadgeVariant(
  level: TaskLogEntry["level"],
): "danger" | "info" | "warning" {
  if (level === "error") {
    return "danger";
  }
  if (level === "warn") {
    return "warning";
  }
  return "info";
}

export function formatFlowStatusLabel(status: string): string {
  return status
    .split(/[_-]+/g)
    .filter(Boolean)
    .map((segment) => segment[0]?.toUpperCase() + segment.slice(1))
    .join(" ");
}

export const CompileRecordingPropertyCard: FC<{
  label: string;
  value: string | null;
}> = ({ label, value }) => (
  <div className="rounded-none border border-slate-200 bg-white/80 p-3 dark:border-white/10 dark:bg-slate-950/40">
    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
      {label}
    </div>
    <div className="mt-2 break-all text-sm font-medium text-slate-900 dark:text-slate-100">
      {value ?? "Unavailable"}
    </div>
  </div>
);

const CompileRecordingTraceMeta: FC<{ log: TaskLogEntry }> = ({ log }) => {
  const metaParts = [
    log.component,
    log.scope,
    log.task_id ? `task ${log.task_id}` : null,
  ].filter((value): value is string => Boolean(value));

  if (metaParts.length === 0) {
    return null;
  }

  return (
    <div className="mt-1 flex flex-wrap gap-2 text-[11px] font-mono uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
      {metaParts.map((part, index) => (
        <span key={index}>{part}</span>
      ))}
    </div>
  );
};

export const CompileRecordingTraceList: FC<{ logs: TaskLogEntry[] }> = ({
  logs,
}) => (
  <div className="space-y-2">
    <SectionHeader
      title="Trace Timeline"
      badge={String(logs.length)}
      icon={
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
          />
        </svg>
      }
    />
    {logs.length === 0 ? (
      <p className="py-6 text-center text-sm text-gray-400 dark:text-gray-500">
        No persisted trace entries yet.
      </p>
    ) : (
      <div className="overflow-hidden rounded-none border border-slate-300 bg-white shadow-[4px_4px_0_rgba(15,23,42,0.08)] dark:border-slate-700 dark:bg-slate-950/50 dark:shadow-[4px_4px_0_rgba(15,23,42,0.24)]">
        <div className="space-y-0 divide-y divide-slate-200 dark:divide-white/5">
          {logs.map((log) => (
            <div
              key={log.id}
              className="grid gap-3 px-4 py-3 md:grid-cols-[120px_minmax(0,1fr)_160px]"
            >
              <div className="space-y-1">
                <Badge size="sm" variant={getTraceBadgeVariant(log.level)}>
                  {log.level}
                </Badge>
                <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">
                  {formatRelativeTime(log.created_at)}
                </div>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                  {log.message}
                </p>
                <CompileRecordingTraceMeta log={log} />
              </div>
              <div className="text-xs text-slate-500 dark:text-slate-400">
                {log.ticket_id ? <div>Issue: {log.ticket_id}</div> : null}
                {log.node_id ? <div>Node: {log.node_id}</div> : null}
                {log.correlation_id ? (
                  <div className="truncate">
                    Correlation: {log.correlation_id}
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);
